import api from './api'
import { PaginationResponse } from '../types/api'
import { Battery } from '../types/battery'
import {
  AdminProductDetail,
  AdminProductFormValues,
  AdminProductMetadata,
  AdminProductSortField,
  AdminProductStatusFilter
} from '../types/adminProducts'

type ApiSuccessResponse<T> = {
  success?: boolean
  data: T
}

type ListParams = {
  search?: string
  status?: AdminProductStatusFilter
  sortBy?: AdminProductSortField
  sortOrder?: 'asc' | 'desc'
  includeInactive?: boolean
  isActive?: boolean
  page?: number
  limit?: number
  brand?: string[]
  category?: string[]
  priceMin?: number
  priceMax?: number
  stockMin?: number
  stockMax?: number
  capacityMin?: number
  capacityMax?: number
  voltage?: number[]
}

export type AdminProductListResponse = {
  products: Battery[]
  pagination: PaginationResponse
  filters: {
    brands: string[]
    categories: string[]
    priceRange: {
      min: number
      max: number
    }
  }
}

const adminProductService = {
  async list(params: ListParams = {}): Promise<AdminProductListResponse> {
    const {
      search,
      status,
      sortBy,
      sortOrder,
      includeInactive,
      isActive,
      page,
      limit,
      brand,
      category,
      priceMin,
      priceMax,
      stockMin,
      stockMax,
      capacityMin,
      capacityMax,
      voltage
    } = params

    // Usar POST para enviar filtros no body
    const body: Record<string, unknown> = {}
    
    if (search) body.search = search
    if (status && status !== 'all') {
      body.isActive = status === 'active'
    }
    if (includeInactive !== undefined) {
      body.includeInactive = includeInactive
    }
    if (isActive !== undefined) {
      body.isActive = isActive
    }
    if (brand && brand.length > 0) body.brand = brand
    if (category && category.length > 0) body.category = category
    if (priceMin !== undefined) body.priceMin = priceMin
    if (priceMax !== undefined) body.priceMax = priceMax
    if (stockMin !== undefined) body.stockMin = stockMin
    if (stockMax !== undefined) body.stockMax = stockMax
    if (capacityMin !== undefined) body.capacityMin = capacityMin
    if (capacityMax !== undefined) body.capacityMax = capacityMax
    if (voltage && voltage.length > 0) body.voltage = voltage
    if (sortBy) body.sortBy = sortBy
    if (sortOrder) body.sortOrder = sortOrder
    if (page) body.page = page
    if (limit) body.limit = limit

    const response = await api.post<ApiSuccessResponse<AdminProductListResponse>>(
      '/admin/products/list',
      body
    )

    return response.data.data
  },

  async getMetadata(): Promise<AdminProductMetadata> {
    const response = await api.get<ApiSuccessResponse<AdminProductMetadata>>(
      '/admin/products/metadata'
    )

    return response.data.data
  },

  async getById(productId: string): Promise<AdminProductDetail> {
    const response = await api.get<ApiSuccessResponse<AdminProductDetail>>(
      `/admin/products/${productId}`
    )

    return response.data.data
  },

  async create(payload: AdminProductFormValues, imageFile?: File): Promise<Battery> {
    const formData = new FormData()
    
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'boolean') {
          formData.append(key, value.toString())
        } else {
          formData.append(key, value.toString())
        }
      } else if (key === 'brandId' || key === 'model') {
        // Para criação, enviar string vazia para que o backend saiba que não há marca/modelo
        formData.append(key, '')
      }
    })

    if (imageFile) {
      formData.append('image', imageFile)
    }

    const response = await api.post<ApiSuccessResponse<Battery>>(
      '/admin/products',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return response.data.data
  },

  async update(
    productId: string,
    payload: AdminProductFormValues
  ): Promise<Battery> {
    // Enviar apenas os dados do produto em JSON, sem imagem
    // A imagem deve ser enviada separadamente usando o método uploadImage
    const body: Record<string, any> = {}
    
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined) {
        body[key] = value
      }
    })

    const response = await api.put<ApiSuccessResponse<Battery>>(
      `/admin/products/${productId}`,
      body
    )

    return response.data.data
  },

  async updateStatus(
    productId: string,
    isActive: boolean
  ): Promise<Battery> {
    const response = await api.patch<ApiSuccessResponse<Battery>>(
      `/admin/products/${productId}/status`,
      { isActive }
    )

    return response.data.data
  },

  async remove(productId: string): Promise<void> {
    await api.delete(`/admin/products/${productId}`)
  },

  async uploadImage(productId: string, imageFile: File): Promise<any> {
    const formData = new FormData()
    formData.append('image', imageFile)

    const response = await api.post<ApiSuccessResponse<any>>(
      `/admin/products/${productId}/image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return response.data.data
  },

  async deleteImage(productId: string, imageId: string): Promise<void> {
    await api.delete(`/admin/products/${productId}/image/${imageId}`)
  },

  async reorderImages(productId: string, imageIds: string[]): Promise<any> {
    const response = await api.patch<ApiSuccessResponse<any>>(
      `/admin/products/${productId}/images/reorder`,
      { imageIds }
    )

    return response.data.data
  },

  async setPrimaryImage(productId: string, imageId: string): Promise<any> {
    const response = await api.patch<ApiSuccessResponse<any>>(
      `/admin/products/${productId}/image/${imageId}/primary`
    )

    return response.data.data
  }
}

export default adminProductService

